import { useFeatureFlag } from '@shared/feature-flags/useFeatureFlag'
import { useEffect, useRef } from 'react'
import { analyticsDate, ANALYTICS_TIME_ZONE } from '@shared/lib/analytics-period'
import { ArrowDownLeft, ArrowUpRight, Info, Lock, ReceiptText } from 'lucide-react'
import { EmptyState, MoneyMovementSummary, MoneyValue, StatusBadge } from '@shared/design-system/ui'
import {
  statementAmountVisible,
  statementEntryForDisplay,
  statementEventTone,
  statementEventTypeLabel,
  statementMeta,
  statementSignPrefix,
  statementTitle,
  statementSummaryMovements,
  statementVisualDirection,
  statementVisualTone,
} from '@entities/wallet'
import type { StatementEntry, StatementVisualDirection } from '@entities/wallet'
import {
  creditAmount,
  debitAmount,
  statementAmount,
  statementGroup,
  statementGroupLabel,
  statementIconTile,
  statementLead,
  statementList,
  statementMain,
  statementMeta as statementMetaClass,
  statementRow,
  statementRowClickable,
  visuallyHidden,
  statementTitle as statementTitleClass,
} from './wallet.css'

type StatementListProps = {
  entries: StatementEntry[]
  onSelect?: (entry: StatementEntry) => void
  filtered?: boolean
  hasMore?: boolean
  loadingMore?: boolean
  onLoadMore?: () => void
}

type StatementGroup = {
  key: string
  label: string
  items: StatementEntry[]
}

function formatDateTime(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: ANALYTICS_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))
}

function dayKey(value: string) {
  return analyticsDate(value)
}

function groupLabel(value: string) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: ANALYTICS_TIME_ZONE,
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  }).format(new Date(value))
}

function groupByDay(entries: StatementEntry[]): StatementGroup[] {
  const groups: StatementGroup[] = []
  for (const entry of entries) {
    const key = dayKey(entry.occurredAt)
    const current = groups.at(-1)
    if (current && current.key === key) {
      current.items.push(entry)
      continue
    }
    groups.push({ key, label: groupLabel(entry.occurredAt), items: [entry] })
  }
  return groups
}

function directionIcon(direction: StatementVisualDirection) {
  if (direction === 'out') {
    return <ArrowUpRight size={15} />
  }
  if (direction === 'held') {
    return <Lock size={14} />
  }
  if (direction === 'info') {
    return <Info size={14} />
  }
  return <ArrowDownLeft size={15} />
}

function tileTone(direction: StatementVisualDirection) {
  if (direction === 'in') {
    return 'credit' as const
  }
  if (direction === 'held') {
    return 'held' as const
  }
  if (direction === 'out') {
    return 'debt' as const
  }
  return 'neutral' as const
}

function amountClassName(direction: StatementVisualDirection) {
  if (direction === 'in') {
    return creditAmount
  }
  if (direction === 'out') {
    return debitAmount
  }
  return undefined
}

function StatementRow({
  entry,
  onSelect,
}: {
  entry: StatementEntry
  onSelect?: (entry: StatementEntry) => void
}) {
  const receiptsExperience = useFeatureFlag('receiptsExperience')
  const displayEntry = statementEntryForDisplay(entry)
  const direction = statementVisualDirection(displayEntry.direction, displayEntry.eventType)
  const toneDirection = statementVisualTone(direction, displayEntry.eventType)
  const sign = statementSignPrefix(direction)
  const clickable = onSelect !== undefined
  const meta = [statementMeta(displayEntry), formatDateTime(displayEntry.occurredAt)]
    .filter((value) => value.length > 0)
    .join(' · ')

  return (
    <div
      data-testid={`statement-row-${entry.id}`}
      className={clickable ? `${statementRow} ${statementRowClickable}` : statementRow}
      {...(clickable
        ? {
            role: 'button',
            tabIndex: 0,
            onClick: () => onSelect(entry),
            onKeyDown: (event: React.KeyboardEvent) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onSelect(entry)
              }
            },
          }
        : {})}
    >
      {clickable ? <span className={visuallyHidden}>Ver detalhes: </span> : null}
      <div className={statementLead}>
        <span className={statementIconTile({ tone: tileTone(toneDirection) })}>
          {directionIcon(direction)}
        </span>
        <div className={statementMain}>
          <span className={statementTitleClass}>
            {statementTitle(displayEntry, receiptsExperience)}
          </span>
          <span className={statementMetaClass}>{meta}</span>
        </div>
      </div>
      <div className={statementAmount}>
        {statementAmountVisible(displayEntry) ? (
          <span className={amountClassName(toneDirection)}>
            {sign ? `${sign} ` : ''}
            <MoneyValue
              amount={displayEntry.amount.amount}
              currency={displayEntry.amount.currency}
              size="md"
            />
          </span>
        ) : null}
        <StatusBadge
          tone={statementEventTone(displayEntry.eventType)}
          label={statementEventTypeLabel(displayEntry.eventType, receiptsExperience)}
          size="sm"
        />
      </div>
    </div>
  )
}

export function StatementList({
  entries,
  onSelect,
  filtered = false,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
}: StatementListProps) {
  const sorted = [...entries].sort((left, right) => {
    const occurred = Date.parse(right.occurredAt) - Date.parse(left.occurredAt)
    return occurred !== 0 ? occurred : right.id.localeCompare(left.id)
  })

  const sentinelRef = useRef<HTMLDivElement | null>(null)
  useEffect(() => {
    const node = sentinelRef.current
    if (!node || !onLoadMore || !hasMore || typeof IntersectionObserver === 'undefined') {
      return undefined
    }
    const observer = new IntersectionObserver(
      (observed) => {
        if (observed.some((entry) => entry.isIntersecting) && !loadingMore) {
          onLoadMore()
        }
      },
      { rootMargin: '360px 0px' },
    )
    observer.observe(node)
    return () => {
      observer.disconnect()
    }
  }, [hasMore, loadingMore, onLoadMore])

  if (sorted.length === 0) {
    if (filtered) {
      return (
        <EmptyState
          icon={<ReceiptText size={22} />}
          title="Nada neste filtro"
          description="Ajuste a busca, o período ou os filtros para ver outros eventos."
        />
      )
    }
    return (
      <EmptyState
        icon={<ReceiptText size={22} />}
        title="Sem eventos"
        description="As vendas, reservas, dívidas e repasses aparecem aqui quando houver movimentação."
      />
    )
  }

  const groups = groupByDay(sorted)

  return (
    <div className={statementList}>
      {groups.map((group) => (
        <div className={statementGroup} key={group.key}>
          <span className={statementGroupLabel}>{group.label}</span>
          {group.items.map((entry) => (
            <StatementRow entry={entry} key={entry.id} onSelect={onSelect} />
          ))}
        </div>
      ))}
      <MoneyMovementSummary movements={statementSummaryMovements(entries)} />
      {onLoadMore && hasMore ? (
        <div ref={sentinelRef} className={statementGroup} aria-hidden={!loadingMore}>
          {loadingMore ? <span className={statementGroupLabel}>Carregando…</span> : null}
        </div>
      ) : null}
    </div>
  )
}
